import { RecipientType } from "encamp-shared/src/constants/submissions";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { runsInteractiveStepper } from "encamp-shared/src/submissions/wasteSubmissionForms";
import {
  ReviewMode,
  SignatureType,
  SubmissionMethod,
  SubmissionType,
} from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCreateSubmission } from "hooks/useSubmissionMutations";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { toUtcMidnightIsoFromDateLike } from "util/dates";

export type CreateStepperSubmissionArgs = {
  submissionType: SubmissionType;
  submissionMethod?: SubmissionMethod | null;
  facilityIds: string[];
  title: string;
  dueDate: DateTime;
  // Links the new submission back to a specific recurring-task occurrence
  // (e.g. clicking a not-started row) instead of creating an unrelated one.
  virtualEventId?: string;
};

/**
 * Creates a stepper (data-entry) submission directly from the Create Submission
 * dialog — document-less, with `initializeStepper` so the backend seeds an
 * OVERVIEW step, which is what marks it as the interactive archetype.
 *
 * This is the only stepper-creation path; the upload flow has no stepper
 * affordance. Reviewers/signature are set later in the review step, so creation
 * uses minimal defaults.
 */
export function useCreateStepperSubmission() {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const interactiveStepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const [createSubmission, { loading }] = useCreateSubmission();

  const createStepperSubmission = useCallback(
    async (args: CreateStepperSubmissionArgs): Promise<string | null> => {
      if (!tenantId) {
        throw new Error("Tenant is required to create a submission");
      }

      const result = await createSubmission({
        variables: {
          input: {
            virtualEventId: args.virtualEventId ?? "",
            tenantId,
            title: args.title,
            submissionType: args.submissionType,
            submissionMethod: args.submissionMethod,
            recipient: RecipientType.REGULATORY_AGENCY,
            notes: "",
            dueDate: toUtcMidnightIsoFromDateLike(args.dueDate) ?? undefined,
            // Interactive submissions default to review ON; document forms start
            // clean until staff opt in on the Review & sign step.
            reviewRequired: runsInteractiveStepper(
              args.submissionType,
              args.submissionMethod,
              interactiveStepperEnabled
            ),
            peerReviewRequired: false,
            reviewMode: ReviewMode.Parallel,
            signatureType: SignatureType.None,
            reviewers: [],
            facilityIds: args.facilityIds,
            comments: [],
            moveToReview: false,
            // The stepper is the data-entry alternative to uploading a filing.
            initializeStepper: true,
          },
        },
      });

      return result.data?.createSubmission?.id ?? null;
    },
    [tenantId, createSubmission, interactiveStepperEnabled]
  );

  return { createStepperSubmission, loading };
}
